import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Paper, Button, Card, CardContent, CardActionArea,
    Chip, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
    FormControl, InputLabel, Select, MenuItem, CircularProgress, LinearProgress,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import { Add as AddIcon, Checklist as ChecklistIcon } from '@mui/icons-material';
import { PageHeader } from '../../components/admin';
import {
    useInventuryQuery,
    useZahajitInventuruMutation,
    useInventarKategorieQuery,
} from '../../api/JsdhNotifikaceService/service/InventarMutations';
import { useActiveTechnikaQuery } from '../../api/JsdhNotifikaceService/service/TechnikaMutations';
import { useAllUsersQuery } from '../../api/JsdhNotifikaceService/service/AdminMutations';
import { useToast } from '../../components/toast/useToast';
import dayjs from 'dayjs';

const formatDate = (d?: string) => d ? dayjs(d).format('DD.MM.YYYY HH:mm') : '—';

const StavChip: React.FC<{ stav: string }> = ({ stav }) => {
    const map: Record<string, { label: string; color: 'info' | 'success' | 'default' }> = {
        PROBIHAJICI: { label: 'Probíhá', color: 'info' },
        DOKONCENA: { label: 'Dokončena', color: 'success' },
        ZRUSENA: { label: 'Zrušena', color: 'default' },
    };
    const { label, color } = map[stav] ?? { label: stav, color: 'default' as const };
    return <Chip label={label} color={color} size="small" />;
};

const InventuraPage: React.FC = () => {
    const navigate = useNavigate();
    const { showToast } = useToast();
    const { data: inventury, isLoading } = useInventuryQuery();
    const zahajitMutation = useZahajitInventuruMutation();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [formNazev, setFormNazev] = useState('');
    const [formTyp, setFormTyp] = useState('KOMPLETNI');
    const [formFiltrVozidloId, setFormFiltrVozidloId] = useState<number | ''>('');
    const [formFiltrKategorieId, setFormFiltrKategorieId] = useState<number | ''>('');
    const [formFiltrClenId, setFormFiltrClenId] = useState<number | ''>('');
    const [filtrTouched, setFiltrTouched] = useState(false);

    // Selecty pro filtry dialogu (vzor: SablonaWizardDialog/KontrolaDialog - jen aktivní vozidla)
    const { data: technika } = useActiveTechnikaQuery();
    const { data: kategorie } = useInventarKategorieQuery();
    const { data: uzivatele } = useAllUsersQuery();

    const resetForm = () => {
        setFormNazev('');
        setFormTyp('KOMPLETNI');
        setFormFiltrVozidloId('');
        setFormFiltrKategorieId('');
        setFormFiltrClenId('');
        setFiltrTouched(false);
    };

    const toFiltrId = (e: SelectChangeEvent<number | ''>): number | '' =>
        e.target.value === '' ? '' : Number(e.target.value);

    const handleTypChange = (typ: string) => {
        setFormTyp(typ);
        setFormFiltrVozidloId('');
        setFormFiltrKategorieId('');
        setFormFiltrClenId('');
        setFiltrTouched(false);
    };

    const handleZahajit = () => {
        if (!formNazev) return;

        if (formTyp === 'VOZIDLO' && !formFiltrVozidloId) {
            setFiltrTouched(true);
            showToast('Vyberte vozidlo pro inventuru podle vozidla.', 'error');
            return;
        }
        if (formTyp === 'KATEGORIE' && !formFiltrKategorieId) {
            setFiltrTouched(true);
            showToast('Vyberte kategorii pro inventuru podle kategorie.', 'error');
            return;
        }
        if (formTyp === 'CLEN' && !formFiltrClenId) {
            setFiltrTouched(true);
            showToast('Vyberte člena pro výstrojní kartu.', 'error');
            return;
        }

        zahajitMutation.mutate({
            nazev: formNazev,
            typ: formTyp,
            ...(formTyp === 'VOZIDLO' && formFiltrVozidloId ? { filtrVozidloId: formFiltrVozidloId } : {}),
            ...(formTyp === 'KATEGORIE' && formFiltrKategorieId ? { filtrKategorieId: formFiltrKategorieId } : {}),
            ...(formTyp === 'CLEN' && formFiltrClenId ? { filtrClenId: formFiltrClenId } : {}),
        }, {
            onSuccess: () => { setDialogOpen(false); resetForm(); },
        });
    };

    return (
        <Box>
            <PageHeader
                title="Inventury"
                icon={<ChecklistIcon />}
                action={
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
                        Nová inventura
                    </Button>
                }
            />

            {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
            ) : !inventury?.length ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <ChecklistIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                    <Typography color="text.secondary">Zatím žádné inventury</Typography>
                </Paper>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {inventury.map((inv: any) => (
                        <Card key={inv.id} variant="outlined">
                            <CardActionArea onClick={() => navigate(`/admin/inventura/${inv.id}`)}>
                                <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Box>
                                        <Typography variant="subtitle1" fontWeight={500}>{inv.nazev}</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {formatDate(inv.datumZahajeni)} · Typ: {inv.typ} · {inv.vytvorilJmeno ?? '—'}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                        {inv.stav === 'PROBIHAJICI' && (
                                            <Box sx={{ width: 120 }}>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={inv.celkemPolozek > 0 ? (inv.nalezenoPolozek / inv.celkemPolozek) * 100 : 0}
                                                />
                                                <Typography variant="caption">{inv.nalezenoPolozek}/{inv.celkemPolozek}</Typography>
                                            </Box>
                                        )}
                                        {inv.stav === 'DOKONCENA' && (
                                            <Typography variant="body2">
                                                {inv.nalezenoPolozek}/{inv.celkemPolozek} · chybí: {inv.chybejiciPolozek}
                                            </Typography>
                                        )}
                                        <StavChip stav={inv.stav} />
                                    </Box>
                                </CardContent>
                            </CardActionArea>
                        </Card>
                    ))}
                </Box>
            )}

            <Dialog open={dialogOpen} onClose={() => { setDialogOpen(false); resetForm(); }} maxWidth="sm" fullWidth>
                <DialogTitle>Nová inventura</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField label="Název" required value={formNazev} onChange={(e) => setFormNazev(e.target.value)} placeholder="Roční inventura 2026" />
                        <FormControl>
                            <InputLabel>Typ</InputLabel>
                            <Select value={formTyp} onChange={(e) => handleTypChange(e.target.value)} label="Typ">
                                <MenuItem value="KOMPLETNI">Kompletní</MenuItem>
                                <MenuItem value="VOZIDLO">Podle vozidla</MenuItem>
                                <MenuItem value="KATEGORIE">Podle kategorie</MenuItem>
                                <MenuItem value="CLEN">Výstrojní karta člena</MenuItem>
                            </Select>
                        </FormControl>

                        {formTyp === 'VOZIDLO' && (
                            <FormControl required error={filtrTouched && !formFiltrVozidloId}>
                                <InputLabel>Vozidlo</InputLabel>
                                <Select
                                    value={formFiltrVozidloId}
                                    onChange={(e) => setFormFiltrVozidloId(toFiltrId(e))}
                                    label="Vozidlo"
                                >
                                    {(technika ?? []).map((t) => (
                                        <MenuItem key={t.id} value={t.id}>{t.volaciZnak} - {t.nazev}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}

                        {formTyp === 'KATEGORIE' && (
                            <FormControl required error={filtrTouched && !formFiltrKategorieId}>
                                <InputLabel>Kategorie</InputLabel>
                                <Select
                                    value={formFiltrKategorieId}
                                    onChange={(e) => setFormFiltrKategorieId(toFiltrId(e))}
                                    label="Kategorie"
                                >
                                    {(kategorie ?? []).map((k) => (
                                        <MenuItem key={k.id} value={k.id}>{k.nazev}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}

                        {formTyp === 'CLEN' && (
                            <FormControl required error={filtrTouched && !formFiltrClenId}>
                                <InputLabel>Člen</InputLabel>
                                <Select
                                    value={formFiltrClenId}
                                    onChange={(e) => setFormFiltrClenId(toFiltrId(e))}
                                    label="Člen"
                                >
                                    {(uzivatele ?? []).map((u) => (
                                        <MenuItem key={u.id} value={u.id}>{u.jmeno}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setDialogOpen(false); resetForm(); }}>Zrušit</Button>
                    <Button variant="contained" onClick={handleZahajit} disabled={!formNazev || zahajitMutation.isPending}>
                        Zahájit
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default InventuraPage;
